import { useEffect, useState } from 'react';
import { getProductTypeName } from '../constants/productTypeNames';
import { apiService } from '../services/apiService';
import { Product } from './useProductsList';

interface FilterOption {
  value: string;
  label: string;
}

export function useFilterOptions(products: Product[]) {
  const [productTypes, setProductTypes] = useState<FilterOption[]>([]);
  const [issuers, setIssuers] = useState<FilterOption[]>([]);
  const [reports, setReports] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Cargar TODOS los filtros disponibles del usuario (sin filtros aplicados)
  useEffect(() => {
    const fetchAllFilterOptions = async () => {
      try {
        setLoading(true);

        const reportsPromise = apiService.getCustomReports().catch(err => {
          console.error('Error fetching custom reports:', err);
          return [] as Array<{ id: string; name: string }>;
        });

        // Obtener una muestra grande de TODOS los productos del usuario (sin filtros)
        // para tener todos los productCodes e issuers disponibles
        let allProducts: any[] = [];
        let currentPage = 1;
        let hasMore = true;
        const pageSize = 100;

        while (hasMore && currentPage <= 20) {
          // Limitar a 20 páginas
          const response = await apiService.getProductsList(currentPage, pageSize, {});
          const pageProducts = response.data || [];
          allProducts = [...allProducts, ...pageProducts];

          hasMore = response.pagination?.hasNextPage || false;
          currentPage++;
        }

        // Extraer productCodes e issuers únicos de TODOS los productos del usuario
        const uniqueProductCodes = new Map<string, string>(); // Map<productCode, label>
        const uniqueIssuers = new Set<string>();

        allProducts.forEach((product: any) => {
          // Usar productCode si está disponible, sino usar productType como fallback
          if (product.productCode && product.productCode.trim() !== '') {
            const code = product.productCode.trim();
            // El label será el nombre del producto (ej: "Capital Protection Certificate")
            const name = getProductTypeName(code);
            uniqueProductCodes.set(code, name);
          } else if (product.productType != null && product.productType !== undefined) {
            // Fallback al productType si no hay productCode
            const type = String(product.productType);
            const name = getProductTypeName(type);
            uniqueProductCodes.set(type, name);
          }

          // issuer viene como string
          if (product.issuer && product.issuer.trim() !== '') {
            uniqueIssuers.add(product.issuer.trim());
          }
        });

        // Convertir a arrays y ordenar
        const productTypesArray: FilterOption[] = Array.from(uniqueProductCodes.entries())
          .sort((a, b) => {
            // Ordenar por código numérico si es posible
            const numA = parseInt(a[0]) || 0;
            const numB = parseInt(b[0]) || 0;
            if (numA !== 0 && numB !== 0) {
              return numA - numB;
            }
            return a[0].localeCompare(b[0]);
          })
          .map(([value, label]) => ({
            value,
            label,
          }));

        const issuersArray: FilterOption[] = Array.from(uniqueIssuers)
          .sort()
          .map(issuer => ({
            value: issuer,
            label: issuer,
          }));

        const customReports = await reportsPromise;
        const reportsArray: FilterOption[] = (customReports || [])
          .filter(report => report?.id != null && report?.name)
          .map(report => ({
            value: String(report.id),
            label: report.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setProductTypes(productTypesArray);
        setIssuers(issuersArray);
        setReports(reportsArray);
      } catch (err: any) {
        console.error('Error fetching all filter options:', err);
        // Si falla, usar los productos actuales como fallback
        if (products && products.length > 0) {
          const uniqueProductCodes = new Map<string, string>();
          const uniqueIssuers = new Set<string>();

          products.forEach((product: any) => {
            if (product.productCode && product.productCode.trim() !== '') {
              const code = product.productCode.trim();
              const name = getProductTypeName(code);
              uniqueProductCodes.set(code, name);
            } else if (product.productType != null) {
              const type = String(product.productType);
              const name = getProductTypeName(type);
              uniqueProductCodes.set(type, name);
            }
            if (product.issuer && product.issuer.trim() !== '') {
              uniqueIssuers.add(product.issuer.trim());
            }
          });

          setProductTypes(
            Array.from(uniqueProductCodes.entries()).map(([v, l]) => ({ value: v, label: l }))
          );
          setIssuers(
            Array.from(uniqueIssuers)
              .sort()
              .map(i => ({ value: i, label: i }))
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllFilterOptions();
  }, []); // Solo se ejecuta una vez al montar

  const reloadReports = async () => {
    try {
      const customReports = await apiService.getCustomReports();
      const reportsArray: FilterOption[] = (customReports || [])
        .filter(report => report?.id != null && report?.name)
        .map(report => ({
          value: String(report.id),
          label: report.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setReports(reportsArray);
    } catch (err) {
      console.error('Error fetching custom reports:', err);
    }
  };

  return {
    productTypes,
    issuers,
    reports,
    loading,
    error: null,
    reloadReports,
  };
}
